import type { Invitation } from '@noconflict/contracts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  invitationErrorMessage,
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from '../../../entities/invitation';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { useFormat } from '../../../shared/i18n/format';
import { Button } from '../../../shared/ui/button';
import { ConfirmDialog } from '../../../shared/ui/confirm-dialog';
import { DataTable, type DataTableColumn } from '../../../shared/ui/data-table';
import { toast } from '../../../shared/ui/sonner';
import { StatusBadge } from '../../../shared/ui/status-badge';

/**
 * Open invitations of the org as RLS shows them: all with `members.manage`, a workspace manager's own
 * otherwise. Resend rotates the link; revoke asks first. Renders nothing while there are none.
 */
export function PendingInvitations({
  org,
  workspaces,
}: {
  org: { id: string; name: string };
  workspaces: readonly { id: string; name: string }[];
}) {
  const { t } = useTranslation('member');
  const { formatDate } = useFormat();
  const invitations = useInvitations(org.id);
  const resend = useResendInvitation(org.id);
  const revoke = useRevokeInvitation(org.id);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const columns = useMemo<DataTableColumn<Invitation>[]>(
    () => [
      {
        id: 'email',
        header: t('columns.email'),
        sortable: true,
        value: (i) => i.email,
        // long addresses wrap, so the actions stay on screen
        cell: (i) => (
          <span className="block max-w-64 font-bold wrap-anywhere text-heading">{i.email}</span>
        ),
      },
      {
        id: 'role',
        header: t('columns.role'),
        value: (i) => i.org_role,
        cell: (i) => t(`roles.${i.org_role}`),
      },
      {
        id: 'access',
        header: t('columns.access'),
        value: () => null,
        cell: (i) => {
          if (i.org_role === 'owner' || i.org_role === 'admin') return t('access.everything');
          if (i.workspace_grants.length === 0) return t('access.none');
          return (
            <ul>
              {i.workspace_grants.map((g) => (
                <li key={g.workspace_id ?? 'all'}>
                  {t('pending.grant', {
                    workspace:
                      g.workspace_id === null
                        ? t('grants.all')
                        : (workspaces.find((w) => w.id === g.workspace_id)?.name ??
                          t('pending.unknownWorkspace')),
                    role: t(`roles.${g.role}`),
                  })}
                </li>
              ))}
            </ul>
          );
        },
      },
      {
        id: 'status',
        header: t('pending.status'),
        value: (i) => i.status,
        cell: (i) => (
          <span className="flex flex-col items-start gap-1">
            <StatusBadge tone={i.status === 'pending' ? 'info' : 'warning'} size="compact">
              {t(`pending.statuses.${i.status === 'pending' ? 'pending' : 'expired'}`)}
            </StatusBadge>
            <span className="text-sm whitespace-nowrap text-muted-foreground">
              {t(i.status === 'pending' ? 'pending.expiresOn' : 'pending.expiredOn', {
                date: formatDate(i.expires_at),
              })}
            </span>
          </span>
        ),
      },
      {
        id: 'actions',
        header: t('columns.actions'),
        value: () => null,
        align: 'right',
        cell: (i) => (
          <span className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              aria-label={t('pending.resendTo', { email: i.email })}
              disabled={resend.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await resend.mutateAsync(i.id);
                  toast.success(t('pending.resent'));
                } catch (e) {
                  setError(invitationErrorMessage(e));
                }
              }}
            >
              {t('pending.resend')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              aria-label={t('pending.revokeTo', { email: i.email })}
              onClick={() => setRevoking(i)}
            >
              {t('pending.revoke')}
            </Button>
          </span>
        ),
      },
    ],
    [t, formatDate, workspaces, resend],
  );

  if (!invitations.data || invitations.data.length === 0) return null;
  return (
    <section className="grid gap-3" aria-labelledby={`pending-${org.id}`}>
      <h3 id={`pending-${org.id}`} className="text-base font-bold text-heading">
        {t('pending.title')}
      </h3>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <DataTable
        caption={t('pending.title')}
        columns={columns}
        data={invitations.data}
        getRowId={(i) => i.id}
        pageSize={10}
      />
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => (open ? null : setRevoking(null))}
        title={t('pending.revokeTitle', { email: revoking?.email ?? '' })}
        description={t('pending.revokeBody')}
        confirmLabel={t('pending.revokeConfirm')}
        cancelLabel={t('pending.revokeCancel')}
        errorMessage={identityErrorMessage}
        onConfirm={async () => {
          if (!revoking) return;
          await revoke.mutateAsync(revoking.id);
          toast.success(t('pending.revoked'));
        }}
      />
    </section>
  );
}
